<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { helpers, maxLength, required, requiredIf } from "@vuelidate/validators";
import axios from "axios";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import ManualAddressForm from "@/components/ExperiencePage/Misc/ManualAddressForm.vue";
import AddressComponent from "@/components/forms/AddressInputComponent/AddressComponent.vue";
import type StripeDepositFormType from "@/components/forms/PayDeposit/StripeDepositForm.vue";
import StripeDepositForm from "@/components/forms/PayDeposit/StripeDepositForm.vue";
import type ButtonWithSpinnerType from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import CheckBox from "@/components/forms/SharedComponents/DarkBlueInputs/Checkbox.vue";
import InputWithButton from "@/components/forms/SharedComponents/DarkBlueInputs/InputWithButton.vue";
import RadioButtons from "@/components/forms/SharedComponents/DarkBlueInputs/RadioButtons.vue";
import TextField from "@/components/forms/SharedComponents/DarkBlueInputs/TextField.vue";

import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import { hasStripeApplicationDepositEnabled, useCustomPriceFees } from "@/composables/authorization.js";
import { usePascalCase } from "@/composables/stringManupulation";
import type { StripeChargePayload } from "@/composables/types/stripe";
import { useNotifications } from "@/composables/useNotifications";
import { useStripe } from "@/composables/useStripe";
import { useTenantTheming } from "@/composables/useTenantTheming";
import { APPLICATION_FEE_DATA, ORDER_STATUS } from "@/constants";
import { getCategoryItemArray, trackPurchase } from "@/util/eventTracker.js";
import { specialPricingOverrideEntityId } from "@/util/specialPricingOverride";
import type { DepositModelData } from "../../../composables/types/deposit";
import { useDepositModels } from "../../../composables/useDepositModels";
import { CHECKOUT_SUBMIT_BUTTON_LABELS } from "../constants";
import type { AddressController, GoogleAddress, GoogleAddressExt, ManualAddress } from "../types/address";
import type { FormIoApplicationSubmission } from "../types/formio";
import type { PaymentServicePayload } from "../types/paymentService";
import type { AppliedBillingAgreement } from "./types";

enum AddressType {
  Current = "Current",
  Permanent = "Permanent",
  Billing = "Billing",
  Other = "Other",
}

const REQUIRED_ERROR_COPY: string = "This field is required.";
const FORM_IO_FORM: string = "paymentform";

const ADDRESS_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: "Current Address", value: AddressType.Current },
  { label: "Permanent Address", value: AddressType.Permanent },
  { label: "Other", value: AddressType.Other },
];

const { sendNotifications } = useNotifications();
const { getSubmissionsDataForFormIo, getStripeChargePayload, getPaymentServicePayload } = useDepositModels();

const store = useStore();
const { secondaryColorClassLinks } = useTenantTheming();

const props = defineProps({
  isReadOnly: {
    type: Boolean,
    default: false,
  },
  existingPaymentData: {
    type: Object,
    default() {
      return {};
    },
  },
  initialCountries: {
    type: Array,
    default: () => [],
  },
  buttonLabel: {
    type: String,
    default: CHECKOUT_SUBMIT_BUTTON_LABELS.application,
  },
  application: {
    type: Object,
    default() {
      return {};
    },
  },
});

const emit = defineEmits(["next-step", "update-order"]);

const isComponentLoading = ref<boolean>(false);
const { registerLoadingState } = props.application;

const APPLICATION_LOADER = "student-pays";

registerLoadingState(APPLICATION_LOADER, isComponentLoading);

const {
  stripePaymentError,
  stripePaymentErrorType,
  paymentAlreadyExist,
  stripeStatusMessage,
  paymentSuccess,
  paymentStatus,
  transactionId,
  cardSectionLoading,
  setApplicationId,
  setStripeComponentRef,
  handleSubmitToStripe,
  checkDepositDataForStripe,
} = useStripe();

const vuelidateState = reactive({
  promoCode: "",
  legalFirstName: props.existingPaymentData?.data?.firstname ?? "",
  legalLastName: props.existingPaymentData?.data?.lastname ?? "",
  sameLegalName: false,
  cancellationPolicyCheckbox: {
    policy: false,
  },
  billingAddress: {
    street_address_1: "",
    city: "",
    state: "",
    country: "",
    countryName: "",
    postal_code: "",
  },
});

const vuelidateRules = {
  promoCode: {
    requiredIf: helpers.withMessage(
      REQUIRED_ERROR_COPY,
      requiredIf(() => isCubaProgram.value),
    ),
  },
  sameLegalName: {},
  legalFirstName: {
    required: helpers.withMessage(REQUIRED_ERROR_COPY, required),
    maxLength: helpers.withMessage(
      ({ $params }) => `Legal First Name must have fewer than ${$params.max} characters.`,
      maxLength(100),
    ),
  },
  legalLastName: {
    required: helpers.withMessage(REQUIRED_ERROR_COPY, required),
    maxLength: helpers.withMessage(
      ({ $params }) => `Legal First Name must have fewer than ${$params.max} characters.`,
      maxLength(100),
    ),
  },
  cancellationPolicyCheckbox: {
    policy: {
      requiredIf: helpers.withMessage(
        REQUIRED_ERROR_COPY,
        requiredIf(() => requiresCancellationPolicyAgreement.value),
      ),
    },
  },
  billingAddress: {
    street_address_1: {
      required: helpers.withMessage("Address is required", required),
    },
    city: {
      required: helpers.withMessage(
        "The address provided doesn't include a city.",
        requiredIf(() => vuelidate.value.billingAddress.address),
      ),
    },
    state: {
      required: helpers.withMessage(
        "The address provided doesn't include a state.",
        requiredIf(() => vuelidate.value.billingAddress.address),
      ),
    },
    country: {
      required: helpers.withMessage(
        "The address provided doesn't include a country.",
        requiredIf(() => vuelidate.value.billingAddress.address),
      ),
    },
    postal_code: {
      required: helpers.withMessage(
        "The address provided doesn't include a postal code.",
        requiredIf(() => vuelidate.value.billingAddress.address),
      ),
    },
  },
};

const vuelidate = useVuelidate(vuelidateRules, vuelidateState);

const currentUser = computed(() => store.getters.getCurrentUser);
const ownerId = computed(() => currentUser.value.formioStudentId || currentUser.value.formioAdvisorId);
const participantId = computed(() => store.state.currentUser?.participantId);
const customerId = computed(() => store.state.customer_id);
const appliedAgreement = computed<AppliedBillingAgreement | undefined>(() => {
  return props.application.appliedAgreement?.value;
});

const hostInstitution = computed(
  () => props.application.session.value?.session_hosting_institutions?.[0]?.entity?.name,
);

const sessionApplicationFee = props.application.sessionApplicationFee;
const sessionFeesBlockingCheckout = props.application.sessionFeesBlockingCheckout;
const withdrawalPolicyUrl = props.application.withdrawalPolicyUrl;
const requiresCancellationPolicyAgreement = props.application.requiresCancellationPolicyAgreement;

const applicationFeeAmountDollars = computed(() => {
  if (props.application.applicationFeePayment.value >= 0) {
    return Number(props.application.applicationFeePayment.value);
  }

  const feeCents = sessionApplicationFee.cents.value;
  const feeCentsAsNumber = Number(feeCents);

  const hasValidSessionFee =
    useCustomPriceFees.value === true &&
    feeCents !== null &&
    feeCents !== undefined &&
    !Number.isNaN(feeCentsAsNumber);

  if (hasValidSessionFee) {
    return Number(sessionApplicationFee.dollars.value);
  }

  return Number(APPLICATION_FEE_DATA.amount);
});

const applicationFeeAmountDisplay = computed(() => applicationFeeAmountDollars.value.toFixed(2));

const depositPayloadData = computed<DepositModelData>(() => {
  return {
    person: { first_name: vuelidateState.legalFirstName, last_name: vuelidateState.legalLastName },
    applicationId: props.application.applicationId.value,
    amount: applicationFeeAmountDollars.value,
    productName: APPLICATION_FEE_DATA.label,
    netsuiteSku: APPLICATION_FEE_DATA.netsuiteSku,
    session: props.application.session.value,
    userData: props.application.userData.value,
    profile: props.application.profile.value,
    ipeds: props.application.ipeds.value,
    ownerId: ownerId.value,
    billingAddress: vuelidateState.billingAddress,
    currentAddress: currentAddress.value,
    permanentAddress: permanentAddress.value,
    customerId: customerId.value,
    hostInstitution: hostInstitution.value,
    contractNumber: appliedAgreement.value?.contract_number ?? null,
    promoCode: vuelidateState.promoCode,
    participantId: participantId.value,
    transactionId: transactionId.value,
    paymentStatus: paymentStatus.value,
    completedManually: addressController.value.billingAddress.showManualForm,
    creditTransactionId: creditTransactionId.value,
    cardExpMonth: cardInformation.value.month,
    cancellationPolicy: requiresCancellationPolicyAgreement.value
      ? vuelidateState.cancellationPolicyCheckbox.policy
      : true,
    financial_product_line_id: props.application.session.value.financial_product_line_id,
    financial_product_type_id: props.application.session.value.financial_product_type_id,
    session_id: props.application.session.value.id,
    special_pricing_override_entity_id: specialPricingOverrideEntityId(),
  };
});

// START ================= PROMO CODES ================================
const promoMessage = ref<string>("");
const applyingPromoCode = ref<boolean>(false);
const validDiscountCode = ref<boolean>(false);
const showDiscountButton = ref<boolean>(false);

const applyPromoCode = async (): Promise<void> => {
  applyingPromoCode.value = true;
  try {
    const promo_response = await axios.post(
      `/discounts/${vuelidateState.promoCode}`,
      {
        user: props.application.userData.value,
      },
      {
        headers: {
          "Content-type": "application/json",
        },
      },
    );
    if (promo_response?.data?.["0"] === 200) {
      validDiscountCode.value = true;
      promoMessage.value = "Code applied successfully";
    } else {
      validDiscountCode.value = false;
      promoMessage.value = "Invalid Code";
    }
  } catch (_error) {
    validDiscountCode.value = false;
    promoMessage.value = "Invalid Code";
  } finally {
    applyingPromoCode.value = false;
  }
};

// END ================= PROMO CODES ================================

// START ================ NAMES AND DATES SECTION ===========================
const handleSameLegalNameCheckbox = (isBillingNameSame = false) => {
  const app = props.application;
  if (isBillingNameSame) {
    vuelidateState.legalFirstName =
      props.application.profile.value.legal_first_name ?? props.application.profile.value.preferred_first_name;
    vuelidateState.legalLastName = props.application.profile.value.last_name;
  } else {
    vuelidateState.legalFirstName = "";
    vuelidateState.legalLastName = "";
  }
};

// END ================ NAMES AND DATES SECTION ===========================

// START ================ ADDRESS SECTION ===========================
const BLANK_ADDRESS: GoogleAddress = {
  street_address_1: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
};

const showSkeletonLoader = ref<boolean>(false);
const addressType = ref<AddressType>(AddressType.Other);

const addressController = ref<AddressController>({
  billingAddress: {
    showManualForm: false, // hides autocomplete input then allows to capture address manually (if true)
    shownDueToError: false, // displays message that address couldn't be validated (if true)
  },
});

const existingPaymentAddress = computed<GoogleAddress>(() => {
  if (!props.existingPaymentData?.data) {
    return BLANK_ADDRESS;
  }
  return {
    street_address_1: props.existingPaymentData.data.address ?? "",
    city: props.existingPaymentData.data.city ?? "",
    state: props.existingPaymentData.data.state ?? "",
    country: props.existingPaymentData.data.country ?? "",
    postal_code: props.existingPaymentData.data.postCode ?? "",
  };
});

const currentAddress = computed<GoogleAddress>(() => {
  const profile = props.application.profile.value;
  if (!profile) return BLANK_ADDRESS;
  return {
    street_address_1: profile.current_address_line_1,
    city: profile.current_address_city,
    state: profile.current_address_state,
    country: profile.current_address_country_id,
    postal_code: profile.current_address_postal_code,
  };
});

const permanentAddress = computed<GoogleAddress>(() => {
  const profile = props.application.profile.value;
  if (!profile) return BLANK_ADDRESS;
  return {
    street_address_1: profile.permanent_address_line_1,
    city: profile.permanent_address_city,
    state: profile.permanent_address_state,
    country: profile.permanent_address_country_id,
    postal_code: profile.permanent_address_postal_code,
  };
});

const updateBillingAddress = (address: GoogleAddress) => {
  vuelidateState.billingAddress = {
    street_address_1: address.street_address_1,
    city: address.city,
    state: address.state,
    postal_code: address.postal_code,
    country: address.country,
    countryName: address.countryName ?? "",
  };
};

const updateBillingAddressManual = (addressInformation: ManualAddress) => {
  updateBillingAddress({
    street_address_1: addressInformation.address ?? "",
    city: addressInformation.address_city ?? "",
    state: addressInformation.address_state ?? "",
    country: addressInformation.address_country_id ?? "",
    countryName: addressInformation.country ?? "",
    postal_code: addressInformation.address_postal_code ?? "",
  });
};

const updateBillingAddressAuto = (addressInformation: GoogleAddressExt) => {
  const { street_address_1, number, iso } = addressInformation;
  updateBillingAddress({
    ...addressInformation,
    street_address_1: `${number ? `${number} ` : ""}${street_address_1 || ""}`,
    country: iso ?? "",
  });
};

const updateBillingAddressByAddress = (updatedAddress: GoogleAddress) => {
  updateBillingAddress(updatedAddress);
};

const updateBillingAddressByType = (addressType: AddressType) => {
  let updatedAddress: GoogleAddress = BLANK_ADDRESS;
  switch (addressType) {
    case AddressType.Current:
      updatedAddress = currentAddress.value;
      break;
    case AddressType.Permanent:
      updatedAddress = permanentAddress.value;
  }
  updateBillingAddressByAddress(updatedAddress);
};

const selectBillingAddressType = (selectedAddressType: string): void => {
  addressType.value = AddressType[selectedAddressType as keyof typeof AddressType];
  updateBillingAddressByType(addressType.value);
};

const saveBillingAddressToProfile = async () => {
  const profile = props.application.profile.value;
  if (!profile) return;
  const data = {
    email: profile.email,

    manual_billing_address_completion: addressController.value.billingAddress.showManualForm,
    billing_address_country_id: vuelidateState.billingAddress.country ?? null,
    billing_address_state: vuelidateState.billingAddress.state ?? null,
    billing_address_city: vuelidateState.billingAddress.city ?? null,
    billing_address_line_1: vuelidateState.billingAddress.street_address_1 ?? null,
    billing_address_postal_code: vuelidateState.billingAddress.postal_code ?? null,
    billing_address_administrative_area_level_2: null,

    majors: profile.majors ?? [],
    minors: profile.minors ?? [],
    colleges: profile.colleges ?? [],
    languages: profile.languages ?? [],
    parents: profile.parents ?? [],
    user_types: profile.user_types ?? [],
    okta_id: profile.okta_id,
  };

  await props.application.updateProfile(profile.okta_id, data);
};

const isUserAddressSelected = computed<boolean>(() => {
  return [AddressType.Current, AddressType.Permanent].includes(addressType.value) || props.isReadOnly;
});

const selectedBillingAddressType = computed<string>(() => {
  return props.isReadOnly ? addressType.value : "";
});
// END ================ ADDRESS SECTION ===========================

// START ============ STRIPE SECTION ==============================

const creditTransactionId = ref<string>("");
const stripeComponentComplete = ref<boolean>(false);
const stripePaymentType = ref<string>("");
const stripeComponentRef = ref<typeof StripeDepositFormType | undefined>();
const paymentSubmitButtonRef = ref<typeof ButtonWithSpinnerType | undefined>();

const submitPayDepositDisabled = computed<boolean>(() => {
  if (props.isReadOnly) {
    return false;
  } else {
    const withdrawalPolicyChecked: boolean =
      !requiresCancellationPolicyAgreement.value || vuelidateState.cancellationPolicyCheckbox.policy;
    return (
      sessionFeesBlockingCheckout.value ||
      vuelidate.value.$invalid ||
      !withdrawalPolicyChecked ||
      !(stripeComponentComplete.value || paymentSuccess.value || validDiscountCode.value || paymentAlreadyExist.value)
    );
  }
});

const enableStripeForm = computed<boolean>(() => {
  return (
    !props.isReadOnly &&
    hasStripeApplicationDepositEnabled.value &&
    !hideCardInformation.value &&
    !paymentAlreadyExist.value &&
    !isCubaProgram.value &&
    !validDiscountCode.value
  );
});

interface CreditCardDetails {
  month?: number;
}

const cardInformation = computed<CreditCardDetails>(() => {
  return {};
});

const submissionsDataForFormIo = computed<FormIoApplicationSubmission>(() => {
  const data = getSubmissionsDataForFormIo(depositPayloadData.value);
  if (props.existingPaymentData?._id) {
    data._id = props.existingPaymentData._id;
  }
  return data;
});

const paymentServicePayload = computed<PaymentServicePayload>(() => {
  const deposit = validDiscountCode.value ? { ...depositPayloadData.value, amount: 0 } : depositPayloadData.value;
  return getPaymentServicePayload(deposit);
});

const stripeChargePayload = computed<StripeChargePayload>(() => {
  return getStripeChargePayload(depositPayloadData.value);
});

const purchaseTransactionId = computed(() => {
  if (vuelidateState.promoCode && vuelidateState.promoCode.trim() !== "") {
    return `${vuelidateState.promoCode.trim()}_${props.application.applicationId.value}`;
  }
  return transactionId.value;
});

const sendPurchaseEvent = () => {
  if (!props.application.orderPricing.value) return;
  const categories = ["Housing", "Internship", "Events", "Additional Fees", "Classes Academic Fees"];

  categories.forEach((category) => {
    const itemsArrays = getCategoryItemArray(
      category,
      props.application.orderPricing.value,
      props.application.primaryOrder.value,
      props.application.session.value,
    );
    if (itemsArrays && itemsArrays.length > 0) {
      itemsArrays.forEach((item) => {
        trackPurchase(
          purchaseTransactionId.value,
          applicationFeeAmountDollars.value,
          "USD",
          vuelidateState.promoCode || "not_set",
          [item],
        );
      });
    }
  });
};

const submitPayDeposit = async () => {
  try {
    isComponentLoading.value = true;
    paymentSubmitButtonRef.value?.startLoading();
    if (props.isReadOnly) {
      emit("next-step");
      return;
    }

    const formioPaymentFormSubmission = await props.application.saveApplicationSubmission(
      FORM_IO_FORM,
      submissionsDataForFormIo.value,
    );

    await saveBillingAddressToProfile();

    if (validDiscountCode.value) {
      if (formioPaymentFormSubmission) {
        sendNotifications("submit", props.application.session.value.salesforce_id);
      }

      if (hasStripeApplicationDepositEnabled.value) {
        await props.application.submitToPaymentService(paymentServicePayload.value);
      }
      sendPurchaseEvent(); // when promoCode is used
      emit("update-order", vuelidate.value.promoCode?.$model);

      paymentSubmitButtonRef.value?.stopLoading();
      emit("next-step");
      return;
    }

    if (hasStripeApplicationDepositEnabled.value) {
      await handleSubmitToStripe(stripeChargePayload.value, stripePaymentType.value);
      if (paymentSuccess.value) {
        sendNotifications("submit", props.application.session.value.salesforce_id);
        sendPurchaseEvent(); // when stripe is used

        await Promise.allSettled([
          props.application.recheckPaymentForApplicationId(),
          props.application.updateOrder(
            props.application.primaryOrder.value.id,
            {
              ...props.application.primaryOrder.value,
              status: ORDER_STATUS.submitted,
              application_fee_payment_id: transactionId.value,
            },
            "StudentPaysDeposit.stripeSubmit",
          ),
        ]);
        emit("next-step");
      }
    }
  } finally {
    paymentSubmitButtonRef.value?.stopLoading();
    isComponentLoading.value = false;
  }
};

const stripePaymentReady = (isReady: boolean) => {
  console.log(`Stripe is ready: ${isReady}`);
};

// END ============ STRIPE SECTION ==============================

const hideCardInformation = computed<boolean>(() => paymentSuccess.value || validDiscountCode.value);
const showAutoComplete = ref<boolean>(true);

const isCubaProgram = computed<boolean>(() => {
  if (props.application.session.value) {
    return props.application.session.value.session_travel_details.some(
      (details) => details.arrival_city?.country?.name.toLowerCase() === "cuba",
    );
  }
  return false;
});

const paymentFee = computed<string>(() => {
  if (validDiscountCode.value || (props.isReadOnly && props.existingPaymentData?.data.promotionCode)) {
    return "0.00";
  }
  return applicationFeeAmountDisplay.value;
});

const submitButton = async () => {
  if (paymentSuccess.value || props.isReadOnly || paymentAlreadyExist.value) {
    props.application.invalidatePaymentForApplicationQuery();
    props.application.invalidateApplicationSubmissions();
    emit("next-step");
  } else {
    await submitPayDeposit();
  }
};

watch(
  vuelidateState,
  async () => {
    if (!hideCardInformation.value) {
      await checkDepositDataForStripe(depositPayloadData.value, isCubaProgram.value);
    }
  },
  { deep: true },
);

onMounted(async () => {
  updateBillingAddressByAddress(existingPaymentAddress.value);
  vuelidateState.cancellationPolicyCheckbox.policy = !!props.existingPaymentData.data?.cancellationPolicy;

  setApplicationId(props.application.applicationId.value);
  if (stripeComponentRef.value) {
    setStripeComponentRef(stripeComponentRef.value);
  }

  await checkDepositDataForStripe(depositPayloadData.value, isCubaProgram.value);
});
</script>
<template>
  <section>
    <div class="grid grid-cols-1 gap-y-7 pt-4">
      <p class="font-medium text-xs">
        Fields marked with (*) are required.
      </p>

      
      <!-- Total Due -->
      <div>
        <h1 class="font-bold text-[28px] uppercase">
          Total Due
        </h1>
        <p>
          Your non-refundable application fee is due now.
        </p>
        <p v-if="isCubaProgram" class="text-xs text-gray-600 font-bold">
          Please call 1-800-844-4124 to make your payment.
        </p>
      </div>

      <!-- Total -->
      <div class="grid grid-cols-2 gap-y-7">
        <div v-if="validDiscountCode" class="col-span-2 grid grid-cols-2 gap-y-2">
          <p class="text-lg font-normal">{{ usePascalCase(APPLICATION_FEE_DATA.typeLabel) }} Amount</p>
          <p class="text-lg font-semibold text-right">${{ applicationFeeAmountDisplay }}</p>
          <p class="text-lg font-normal">Promo Code Discount</p>
          <p class="text-lg font-semibold text-right">-${{ applicationFeeAmountDisplay }}</p>
        </div>
        <div>
          <h1 class="font-bold text-[28px] uppercase">
            {{ isReadOnly ? "Total Paid" : "Total" }}
          </h1>
          <p
            v-if="!showDiscountButton && !isReadOnly"
            :class="`font-bold text-xs uppercase underline cursor-pointer ${secondaryColorClassLinks}`"
            @click="showDiscountButton = true"
          >
            + Add promo code
          </p>
        </div>
        <skeleton-loader
          v-if="application.applicationFeePaymentIsLoading.value || sessionFeesBlockingCheckout"
          rounded
          style="width: 30%; justify-self: end;"
        />
        <h1 v-else data-testid="total-amount" :class="`font-bold text-[28px] ${secondaryColorClassLinks} uppercase text-right`">
          ${{ paymentFee }}
        </h1>
        <!-- Promotional Code -->
        <input-with-button
          v-if="showDiscountButton"
          id="promoCode"
          data-testid="promoCodeButton"
          v-model.trim="vuelidateState.promoCode"
          class="col-span-2"
          label="Promo Code"
          placeholder="Enter promo code"
          :vuelidate-field="vuelidate.promoCode"
          :disable-button="validDiscountCode || applyingPromoCode"
          :is-loading="applyingPromoCode"
          type="text"
          @button-clicked="applyPromoCode"
        />
        <p v-if="isCubaProgram" class="text-xs col-span-2 text-gray-600 font-bold">
          Please call 1-800-844-4124 to make your payment and you will be given a promotion code to submit your
          application deposit.
        </p>
        <p
          v-if="promoMessage"
          class="col-span-2"
          :class="promoMessage && !validDiscountCode ? 'error text-error-900' : 'success text-success-900'"
        >
          {{ promoMessage }}
        </p>
      </div>

      <hr class="mt-4 mb-4" />
      <template v-if="!application.applicationFeePaymentIsLoading.value && !sessionFeesBlockingCheckout">
        <!-- Billing Information -->
        <h1 class="font-bold text-[28px] uppercase">
          Billing Information
        </h1>

        <!-- Same as legal name-->
        <check-box
          id="sameLegalName"
          data-testid="sameLegalNameCheckbox"
          v-model="vuelidateState.sameLegalName"
          :vuelidate-field="vuelidate.sameLegalName"
          :is-disabled="isReadOnly"
          @update:model-value="handleSameLegalNameCheckbox"
        >
          <template #label>
            Name is same as legal name.
          </template>
        </check-box>

        <!-- First name -->
        <text-field
          id="firstname"
          data-testid="firstnameField"
          v-model.trim="vuelidateState.legalFirstName"
          label="First Name*"
          placeholder="Enter Your First Name"
          :vuelidate-field="vuelidate.legalFirstName"
          :disabled="vuelidateState.sameLegalName || isReadOnly"
          :show-errors="true"
        />

        <!-- Last name -->
        <text-field
          id="lastname"
          data-testid="lastnameField"
          v-model.trim="vuelidateState.legalLastName"
          label="Last Name*"
          placeholder="Enter Your Last Name"
          :vuelidate-field="vuelidate.legalLastName"
          :disabled="vuelidateState.sameLegalName || isReadOnly"
          :show-errors="true"
        />

        <!-- Billing Address -->
        <h1 class="font-semibold text-[18px]">
          Billing address is the same as:
        </h1>
        <radio-buttons
          v-model="selectedBillingAddressType"
          data-testid="billingAddressCopyRadio"
          :options="ADDRESS_TYPE_OPTIONS"
          :group-name="'address'"
          :disable-group="isReadOnly"
          @option-choosed="selectBillingAddressType"
        />

        <!-- Billing Address (Autocomplete)-->
        <address-component
          v-if="showAutoComplete && !addressController.billingAddress.showManualForm"
          id="billingAddress"
          data-testid="billingAddress"
          label="Billing Address*"
          template-name="v3"
          :vuelidate-field="vuelidate.billingAddress"
          :initial-address-info="vuelidateState.billingAddress"
          :disable-input="isUserAddressSelected"
          @update-address-info="updateBillingAddressAuto"
        />

        <skeleton-loader v-if="showSkeletonLoader && isReadOnly" rounded />
        <!-- User will input their own billing address -->
        <manual-address-form
          v-if="addressController.billingAddress.showManualForm && !showSkeletonLoader"
          data-testid="manualAddressForm"
          :initial-countries="initialCountries"
          :parent-object-label="'Billing Address'"
          :show-error="addressController.billingAddress.shownDueToError"
          :initial-address="vuelidateState.billingAddress.street_address_1"
          :initial-postal-code="vuelidateState.billingAddress.postal_code"
          :initial-city="vuelidateState.billingAddress.city"
          :initial-state="vuelidateState.billingAddress.state"
          :initial-country-id="vuelidateState.billingAddress.country"
          :initial-country-label="vuelidateState.billingAddress.countryName"
          :disabled="isReadOnly"
          @update-address-object="updateBillingAddressManual"
        />
      </template>

      <!-- Stripe form component -->
      <stripe-deposit-form
        v-show="enableStripeForm"
        ref="stripeComponentRef"
        data-testid="stripeDepositFormPlaceholder"
        :form-loading="cardSectionLoading"
        @ready="stripePaymentReady"
        @details-complete="(isComplete) => (stripeComponentComplete = isComplete)"
        @payment-type="(pType) => (stripePaymentType = pType)"
      />

      <!-- Stripe Errors -->
      <div
        v-if="hasStripeApplicationDepositEnabled && !hideCardInformation && stripePaymentError"
        data-testid="stripeErrorType"
        :class="secondaryColorClassLinks"
      >
        <p>{{ stripePaymentErrorType }}</p>
      </div>
      <!-- END Stripe Errors -->

      <!-- Stripe status message -->
      <div
        v-if="hasStripeApplicationDepositEnabled && !hideCardInformation && stripeStatusMessage && !stripePaymentError"
        data-testid="stripeErrorMessage"
      >
        <p>{{ stripeStatusMessage }}</p>
      </div>
      <!-- END Stripe status message -->

      <!-- Cancellation Policy -->
      <check-box
        v-if="requiresCancellationPolicyAgreement"
        id="cancellationPolicy"
        data-testid="cancellationPolicyCheckbox"
        v-model="vuelidate.cancellationPolicyCheckbox.policy.$model"
        :vuelidate-field="vuelidate.cancellationPolicyCheckbox.policy"
        :is-disabled="isReadOnly"
      >
        <template #label>
          I agree to the
          <a :class="`underline ${secondaryColorClassLinks}`" :href="withdrawalPolicyUrl" target="_blank"
            >Withdrawal Policy.</a
          >*
        </template>
      </check-box>

      <button-with-spinner
        id="paymentSubmitButton"
        ref="paymentSubmitButtonRef"
        data-testid="paymentSubmitButton"
        type="submit"
        variant="secondary"
        variant-type="block"
        button-height="min-h-[58px] md:min-h-[60px] mt-4"
        :grey-disabled-class="true"
        :disabled="submitPayDepositDisabled"
        @click="submitButton"
      >
        <div class="flex items-center justify-center">
          <span class="pr-3 uppercase">
            {{ props.buttonLabel }}
          </span>
          <arrow-right custom-class="w-4 h-4" />
        </div>
      </button-with-spinner>
    </div>
  </section>
</template>
